import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * RolePicker — multi-select for tenant + global roles.
 *
 * Used by both InviteUserModal (initial role set on invite) and the Roles
 * tab inside UserDetailDrawer (replace existing role set). The wire format
 * is `role_ids: string[]` — the API contract for both endpoints.
 *
 * Why a hand-rolled checkbox list and not a fancy combobox: the role count
 * per tenant is bounded (10-20 in the worst tenant after Slice 3.5), so a
 * scroll-free flat list reads faster than the autocomplete UX. The list
 * loads from `/admin/rbac/roles` which already serves the role-selector on
 * the RBAC Insights page; no new endpoint required.
 */
import { useQuery } from '@tanstack/react-query';
import { t } from '@/lib/i18n';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/lib/api';
export function RolePicker({ value, onChange, disabled, }) {
    const roles = useQuery({
        queryKey: ['rbac-roles'],
        queryFn: () => api.get('/admin/rbac/roles'),
    });
    if (roles.isLoading) {
        return _jsx("div", { className: "text-sm text-slate-500", children: "Loading roles\u2026" });
    }
    if (roles.isError) {
        return (_jsxs("div", { className: "text-sm text-rose-600", children: ["Failed to load roles: ", roles.error.message] }));
    }
    const rows = roles.data ?? [];
    const selected = new Set(value);
    const toggle = (roleId) => {
        const next = new Set(selected);
        if (next.has(roleId))
            next.delete(roleId);
        else
            next.add(roleId);
        onChange(Array.from(next));
    };
    return (_jsxs("div", { className: "rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900", children: [rows.length === 0 && (_jsx("div", { className: "px-4 py-6 text-center text-sm text-slate-500", children: t('No roles configured for this tenant.') })), _jsx("ul", { className: "divide-y divide-slate-100 dark:divide-slate-800", children: rows.map((r) => {
                    const isSelected = selected.has(r.id);
                    return (_jsx("li", { children: _jsxs("label", { className: [
                                'flex cursor-pointer items-start gap-3 px-4 py-3 transition',
                                disabled
                                    ? 'cursor-not-allowed opacity-60'
                                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/60',
                            ].join(' '), children: [_jsx("input", { type: "checkbox", className: "mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500", checked: isSelected, disabled: disabled, onChange: () => toggle(r.id) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm font-medium text-slate-900 dark:text-slate-100", children: r.name }), r.is_system && (_jsx(Badge, { className: "text-[10px]", children: "system" }))] }), r.description && (_jsx("div", { className: "mt-0.5 text-xs text-slate-500", children: r.description }))] })] }) }, r.id));
                }) })] }));
}
